import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import styled from 'styled-components';

import { Flex } from 'deprecated-enkel';
import { AuthContext } from 'contexts/AuthContext';

import { TotemButton } from 'ui/Button';
import { InlineError } from 'ui/InlineError';

import { Logo } from 'components/Logo';

import { analyticsEvents, identify, track } from 'tracking/segment';

import { SPYEE_TOKEN, SPYEE_REFRESH_TOKEN } from 'constants/index';
import { validateEmail, HandleNotification } from 'utils';

import { TotemLabel, TotemInput } from 'styles';
import {
    LoginBottomHalfStyle,
    LoginFormStyle,
    LoginStyle,
    LoginTopHalfStyle,
    LoginWrapperStyle,
    BorderBottomLink,
    BorderBottomText,
    RecoverPasswordLink,
} from 'styles/pages/login.styles';
import { SignupTitleStyle } from 'styles/pages/signup.styles';
import type { AuthContextType } from 'contexts/AuthContext';
import { useSetLoginMutation } from 'data/__generated__';
import { SignupFormValues } from 'pages/Signup/Signup.types';

export const Login = () => {
    const {
        authActions: { login },
    } = useContext(AuthContext) as AuthContextType;
    const [loginMutation] = useSetLoginMutation();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<SignupFormValues>();

    const handleLogin = async ({ email, password }: SignupFormValues) => {
        window.TOTEM.storage.remove(SPYEE_TOKEN);
        window.TOTEM.storage.remove(SPYEE_REFRESH_TOKEN);
        const trimmedEmail = email && email.trim();

        try {
            const response = await loginMutation({
                variables: { email: trimmedEmail, password },
            });
            const signin = response?.data?.signin;
            if (signin?.user && signin.token && signin.refreshToken) {
                identify({
                    createdAt: signin.user.createdAt / 1000,
                    email: signin.user.username,
                    id: signin.user._id ?? '',
                    name: `${signin.user.profile.firstname ?? ''} ${signin.user.profile.lastname ?? ''}`,
                });
                track(analyticsEvents.LOG_IN);
                await login(signin.token, signin.refreshToken);
            }
        } catch (error) {
            HandleNotification(error);
        }
    };

    return (
        <LoginStyle>
            <LoginTopHalfStyle>
                <Logo dark />
            </LoginTopHalfStyle>
            <LoginBottomHalfStyle>
                <LoginWrapperStyle>
                    <SignupTitleStyle>Heureux de vous revoir</SignupTitleStyle>
                    <LoginFormStyle onSubmit={handleSubmit(handleLogin)}>
                        <TotemLabel color="white">
                            <span>Email Professionnel</span>
                            <TotemInput
                                placeholder="Entrez votre email pro"
                                autoFocus
                                data-test="login-username"
                                {...register('email', {
                                    required: true,
                                    validate: validateEmail('Votre mail n’est pas valide'),
                                })}
                            />
                            <InlineError display={errors.email} />
                        </TotemLabel>
                        <TotemLabel color="white">
                            <TotemLabelLine>
                                {' '}
                                <span>Mot de passe</span>
                                <RecoverPasswordLink to="/recover-password">Mot de passe oublié ?</RecoverPasswordLink>
                            </TotemLabelLine>

                            <TotemInput
                                placeholder="Entrez votre mot de passe"
                                type="password"
                                autoComplete="current-password"
                                data-test="login-password"
                                {...register('password', { required: true })}
                            />

                            <InlineError display={errors.password} />
                        </TotemLabel>
                        <Flex $alignItems="center" $direction="column">
                            <TotemButton
                                disabled={isSubmitting}
                                $margins={[1, 0]}
                                type="submit"
                                data-test="login-submit"
                            >
                                {isSubmitting ? <ClipLoader color="white" size="16px" /> : 'Se connecter'}
                            </TotemButton>
                            <BorderBottomText>
                                Nouveau chez TOTEM ? <BorderBottomLink to="/signup">Créer un compte</BorderBottomLink>
                            </BorderBottomText>
                        </Flex>
                    </LoginFormStyle>
                </LoginWrapperStyle>
            </LoginBottomHalfStyle>
        </LoginStyle>
    );
};

const TotemLabelLine = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;
